import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAccessControl } from "@/hooks/useAccessControl";
import { getWidgetById } from "@/utils/widgets";
import { selectTemplate } from "../templates";
import { buildLayoutExportData } from "../templates/packLayout";
import { applyLayoutData } from "../templates/applyTemplate";
import { CARD, CTA_GHOST, CTA_PRIMARY, DISPLAY, SECTION_LABEL } from "../ui/theme";
import type { OnboardingAnswers } from "../types";

interface StepDashboardProps {
  answers: OnboardingAnswers;
  selectedWatchlistIds: number[];
  /** Called with the applied template id (or "blank") once done. */
  onComplete: (appliedTemplateId: string) => void;
}

function baseIdOf(id: string): string {
  if (getWidgetById(id)) return id;
  const dashIdx = id.indexOf("-");
  return dashIdx === -1 ? id : id.substring(0, dashIdx);
}

/**
 * Welcome flow step 3: show the template matched to the user's profile and
 * build it. Everything shown is fully usable on their plan.
 */
export default function StepDashboard({
  answers,
  selectedWatchlistIds,
  onComplete,
}: StepDashboardProps) {
  const { hasAccessToWidget } = useAccessControl();
  const [building, setBuilding] = useState(false);

  const template = useMemo(() => selectTemplate(answers), [answers]);

  const widgetSummaries = useMemo(() => {
    const seen = new Set<string>();
    return template.widgets
      .map((spec) => getWidgetById(baseIdOf(spec.id)))
      .filter((w): w is NonNullable<typeof w> => !!w)
      .filter((w) => {
        const label = w.displayName ?? w.name;
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
      });
  }, [template]);

  const handleBuild = () => {
    setBuilding(true);
    try {
      const data = buildLayoutExportData(template.name, template.widgets);
      applyLayoutData(data, {
        hasAccessToWidget,
        highlightWatchlistIds: selectedWatchlistIds,
        repackOnDrop: true,
      });
      toast.success(`Your "${template.name}" dashboard is ready`);
      onComplete(template.id);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to build dashboard"
      );
      setBuilding(false);
    }
  };

  return (
    <div>
      <div className={`${CARD} p-4`}>
        <div className={`${DISPLAY} text-[17px] text-white`}>{template.name}</div>
        <p className="mt-1.5 text-[13px] leading-snug text-gray-400">
          {template.description}
        </p>

        <h3 className={`${SECTION_LABEL} mt-4`}>Widgets</h3>
        <ul className="mt-2 space-y-1.5">
          {widgetSummaries.map((widget) => (
            <li
              key={widget.id}
              className="flex items-start gap-2 text-[13px] leading-snug text-gray-300"
            >
              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-squawk-blue" />
              <span>
                <span className="font-medium text-gray-100">
                  {widget.displayName ?? widget.name}
                </span>
                {widget.description && (
                  <span className="text-gray-500"> — {widget.description}</span>
                )}
              </span>
            </li>
          ))}
        </ul>

        {selectedWatchlistIds.length > 0 && (
          <p className="mt-4 border-t border-white/[0.06] pt-3 text-xs text-squawk-blue">
            {selectedWatchlistIds.length} watchlist
            {selectedWatchlistIds.length > 1 ? "s" : ""} highlighted in the wire
          </p>
        )}
      </div>

      <div className="mt-7 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleBuild}
          disabled={building}
          className={CTA_PRIMARY}
        >
          {building ? "Building…" : "Build my dashboard"}
        </button>
        <button
          type="button"
          onClick={() => onComplete("blank")}
          disabled={building}
          className={CTA_GHOST}
        >
          Use the default
        </button>
      </div>
    </div>
  );
}
